import Box from "@mui/material/Box"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Divider from "@mui/material/Divider"
import IconButton from "@mui/material/IconButton"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FunctionComponent } from "react"
import { useCallback, useState } from "react"
import { useFormContext } from "react-hook-form"

import { AddProductDialogStep } from "../../types"
import { ProductInfoForm } from "../ProductInfoForm"
import { ProductSpecificsForm } from "../ProductSpecificsForm"

import { Button } from "@/components/common/Button"
import type { ProductCreateInput } from "@/graphql/codegen/graphql"
import type { ProductAddFormValues } from "@/screens/Products/schemas/productFormSchema"
import { ProductInfoFormSchema, ProductSpecificsFormSchema } from "@/screens/Products/schemas/productFormSchema"
import type { DialogCloseAction } from "@/types/common"

interface AddProductDialogProps {
  open: boolean
  onClose: (action: DialogCloseAction) => void
  onSubmit: (data: ProductCreateInput) => void
}

const AddProductDialog: FunctionComponent<AddProductDialogProps> = ({ open, onClose, onSubmit }) => {
  const [step, setStep] = useState<AddProductDialogStep>(AddProductDialogStep.PRODUCT_INFO)

  const {
    watch,
    reset,
    handleSubmit,
    formState: { isValid },
  } = useFormContext<ProductAddFormValues>()
  const formValues = watch()

  const onNextStep = useCallback(async () => {
    setStep(AddProductDialogStep.PRODUCT_SPECIFICS)
  }, [])

  const onBackClick = useCallback(() => {
    setStep(AddProductDialogStep.PRODUCT_INFO)
  }, [])

  const handleOnSubmit = useCallback(
    async (value: ProductAddFormValues) => {
      onSubmit(value as ProductCreateInput)
      setStep(AddProductDialogStep.PRODUCT_INFO)
      onClose("success")
    },
    [onClose, onSubmit]
  )

  const handleDialogClose = useCallback(
    (_?: unknown, reason: DialogCloseAction = "cancel") => {
      reset()
      setStep(AddProductDialogStep.PRODUCT_INFO)
      onClose(reason)
    },
    [reset, onClose]
  )

  const isProductInfoValid = ProductInfoFormSchema.safeParse(formValues).success
  const isProductSpecificsValid = ProductSpecificsFormSchema.safeParse(formValues).success

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth>
      <DialogTitle className='pl-10 font-normal text-primary'>Add Products</DialogTitle>

      <IconButton aria-label='close' onClick={handleDialogClose} className='absolute right-4 top-4 text-primary'>
        <CloseOutlined />
      </IconButton>

      <Divider />

      <DialogContent classes={{ root: "p-10" }}>
        {step === AddProductDialogStep.PRODUCT_INFO && <ProductInfoForm />}
        {step === AddProductDialogStep.PRODUCT_SPECIFICS && <ProductSpecificsForm productInfo={formValues} />}
      </DialogContent>

      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={handleDialogClose}>
          Cancel
        </Button>

        {step === AddProductDialogStep.PRODUCT_INFO && (
          <Button onClick={onNextStep} disabled={!isProductInfoValid}>
            Continue
          </Button>
        )}

        {step === AddProductDialogStep.PRODUCT_SPECIFICS && (
          <Box>
            <Button variant='primary' appearance='text' className='mr-6' onClick={onBackClick}>
              Go Back
            </Button>
            <Button disabled={!isProductSpecificsValid || !isValid} onClick={handleSubmit(handleOnSubmit)}>
              Save
            </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default AddProductDialog
